import { useMutation, useQueryClient } from '@tanstack/react-query'
import { removeSlot, upsertSlot } from '@/hooks/binders/binderCache'
import { bindersQueries } from '@/hooks/binders/binderQueries'
import { withIdempotency } from '@/hooks/withIdempotency'
import { setBinderSlot, clearBinderSlot, moveBinderSlot } from '@/services/api/binders'
import type { BinderResponse, BinderSlotResponse } from '@/types'

const MUTATION_KEYS = {
  setSlot: ['mutation', 'binder', 'setSlot'] as const,
  clearSlot: ['mutation', 'binder', 'clearSlot'] as const,
  moveSlot: ['mutation', 'binder', 'moveSlot'] as const,
}

// All binder slot writes share one replay scope so queued offline edits
// replay strictly in order (a move depends on prior set/clear state). On
// settle they reconcile the binder list + detail views — used by the replay
// defaults in main.tsx, which have no hook callbacks of their own.
const BINDER_SLOT_SCOPE = { id: 'binder-slots' } as const
const BINDER_SETTLED_KEYS = [['binders'], ['binder']] as const

/**
 * @public Never imported by name: consumed at runtime by `lib/offlineMutations.ts`
 * (import.meta.glob over hooks/**) to register offline-replay defaults in
 * main.tsx. Un-exporting this silently breaks replay.
 */
export const binderMutations = {
  setSlot: {
    mutationKey: MUTATION_KEYS.setSlot,
    mutationFn: ({
      id,
      pageNumber,
      position,
      cardId,
      idempotencyKey,
    }: {
      id: number
      pageNumber: number
      position: number
      cardId: number
      idempotencyKey?: string
    }) => setBinderSlot(id, pageNumber, position, cardId, idempotencyKey),
    offlineReplay: true,
    scope: BINDER_SLOT_SCOPE,
    settledInvalidation: BINDER_SETTLED_KEYS,
  },
  clearSlot: {
    mutationKey: MUTATION_KEYS.clearSlot,
    mutationFn: ({
      id,
      pageNumber,
      position,
      idempotencyKey,
    }: {
      id: number
      pageNumber: number
      position: number
      idempotencyKey?: string
    }) => clearBinderSlot(id, pageNumber, position, idempotencyKey),
    offlineReplay: true,
    scope: BINDER_SLOT_SCOPE,
    settledInvalidation: BINDER_SETTLED_KEYS,
  },
  moveSlot: {
    mutationKey: MUTATION_KEYS.moveSlot,
    mutationFn: ({
      id,
      args,
      idempotencyKey,
    }: {
      id: number
      args: { fromPageNumber: number; fromPosition: number; toPageNumber: number; toPosition: number }
      idempotencyKey?: string
    }) => moveBinderSlot(id, args, idempotencyKey),
    offlineReplay: true,
    scope: BINDER_SLOT_SCOPE,
    settledInvalidation: BINDER_SETTLED_KEYS,
  },
}

export function useSetBinderSlot() {
  const queryClient = useQueryClient()
  type Vars = { id: number; pageNumber: number; position: number; cardId: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.setSlot,
    mutationFn: ({ id, pageNumber, position, cardId, idempotencyKey }: Vars) =>
      setBinderSlot(id, pageNumber, position, cardId, idempotencyKey),
    onSuccess: (resp: BinderSlotResponse, { id, pageNumber, position }) =>
      upsertSlot(queryClient, id, pageNumber, position, resp.slot),
  })
  return withIdempotency(mutation)
}

export function useClearBinderSlot() {
  const queryClient = useQueryClient()
  type Vars = { id: number; pageNumber: number; position: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.clearSlot,
    mutationFn: ({ id, pageNumber, position, idempotencyKey }: Vars) =>
      clearBinderSlot(id, pageNumber, position, idempotencyKey),
    onSuccess: (_void, { id, pageNumber, position }) => removeSlot(queryClient, id, pageNumber, position),
  })
  return withIdempotency(mutation)
}

export function useMoveBinderSlot() {
  const queryClient = useQueryClient()
  type Vars = {
    id: number
    args: { fromPageNumber: number; fromPosition: number; toPageNumber: number; toPosition: number }
    idempotencyKey?: string
  }
  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.moveSlot,
    mutationFn: ({ id, args, idempotencyKey }: Vars) => moveBinderSlot(id, args, idempotencyKey),
    onSuccess: (resp: BinderResponse, { id }) => {
      queryClient.setQueryData<BinderResponse>(bindersQueries.detail.queryKey(id), resp)
    },
  })
  return withIdempotency(mutation)
}
